import React from 'react';
import { CharacterStats, StatType } from '../../../../../shared/CharacterTypes';
import type { ProgressionCostOfferWire } from '../../../../../shared/types/ProgressionCost';
import { Button } from '../../../primitives/Button';
import { getStatAbbreviation, getStatDescription } from './StatHelpers';
import { StatLedgerRow } from './StatLedgerRow';

interface PrimaryStatsPanelProps {
  stats: CharacterStats;
  statPoints: number;
  characterClass?: string;
  onStatIncrease?: (stat: StatType, amount?: number) => void;
  playerGold?: number;
  statRespecOffer?: ProgressionCostOfferWire | null;
  onRespecStats?: () => void;
}

const PRIMARY_STATS = [
  { stat: StatType.STRENGTH, label: 'Strength' },
  { stat: StatType.DEXTERITY, label: 'Dexterity' },
  { stat: StatType.INTELLIGENCE, label: 'Intelligence' },
  { stat: StatType.LUCK, label: 'Luck' },
] as const;

export const PrimaryStatsPanel: React.FC<PrimaryStatsPanelProps> = ({
  stats,
  statPoints,
  characterClass,
  onStatIncrease,
  playerGold = 0,
  statRespecOffer = null,
  onRespecStats,
}) => {
  const respecCost = statRespecOffer?.quote.cost ?? 0;
  const needsGold = statRespecOffer != null && playerGold < respecCost;

  return (
    <section className="character-stat-section" aria-labelledby="character-primary-stats-heading">
      <h3 id="character-primary-stats-heading">Primary</h3>
      <p className="character-stat-section__intro">
        Spendable attributes. Allocate points individually or in bulk.
      </p>
      <div className="character-stat-list" role="list">
        {PRIMARY_STATS.map(({ stat, label }) => (
          <StatLedgerRow
            key={stat}
            statType={stat}
            stats={stats}
            statPoints={statPoints}
            characterClass={characterClass}
            abbreviation={getStatAbbreviation(stat)}
            label={label}
            value={stats[stat]}
            description={getStatDescription(stat)}
            action={
              <div className="character-stat-action-group">
                <Button
                  className="character-stat-action"
                  variant="brass"
                  size="sm"
                  disabled={statPoints <= 0 || !onStatIncrease}
                  aria-label={`Spend one point on ${label}`}
                  onClick={() => onStatIncrease?.(stat)}
                >
                  +1
                </Button>
                {statPoints >= 5 && (
                  <Button
                    className="character-stat-action"
                    variant="brass"
                    size="sm"
                    disabled={!onStatIncrease}
                    aria-label={`Spend 5 points on ${label}`}
                    onClick={() => onStatIncrease?.(stat, 5)}
                  >
                    +5
                  </Button>
                )}
                {statPoints > 1 && (
                  <Button
                    className="character-stat-action"
                    variant="brass"
                    size="sm"
                    disabled={!onStatIncrease}
                    aria-label={`Spend max (${statPoints}) points on ${label}`}
                    onClick={() => onStatIncrease?.(stat, statPoints)}
                  >
                    Max
                  </Button>
                )}
              </div>
            }
          />
        ))}
      </div>
      {statRespecOffer && (
        <Button
          variant="outline"
          size="sm"
          fullWidth
          disabled={!statRespecOffer.available || needsGold || !onRespecStats}
          onClick={onRespecStats}
          data-testid="stat-respec"
          style={{ marginTop: 10 }}
        >
          {needsGold ? `Need ${respecCost} Gold` : `Respec Stats — ${respecCost} Gold`}
        </Button>
      )}
    </section>
  );
};
